import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';

export const CompanyContext = createContext({ company: null, currency: 'AED', symbol: 'AED' });

export function CompanyProvider({ children }) {
  const { user } = useContext(AuthContext);
  const [company, setCompany] = useState(null);
  // Track which user we have successfully fetched the company for. This lets
  // us derive `initialized` *synchronously* on the same render that `user`
  // transitions from null → truthy, avoiding a race where LanguageBridge sees
  // `ready=true, arabicAvailable=false` for one render and stomps localStorage.
  const [fetchedForUserId, setFetchedForUserId] = useState(null);

  useEffect(() => {
    if (!user) { setCompany(null); setFetchedForUserId(null); return; }
    if (fetchedForUserId === user.id) return;
    axios.get(`${API}/companies`, { withCredentials: true })
      .then(r => setCompany(r.data.find(c => c.id === user.company_id) || r.data[0] || null))
      .catch(() => {})
      .finally(() => setFetchedForUserId(user.id));
  }, [user, fetchedForUserId]);

  const initialized = !user || fetchedForUserId === user.id;

  const features = company?.features || {};
  const hasFeature = (key) => {
    if (!company) return false;
    // Default-on features when features map is missing
    if (Object.keys(features).length === 0) return true;
    return !!features[key];
  };

  const value = {
    company,
    initialized,
    currency: company?.currency || 'AED',
    symbol: company?.currency_symbol || company?.currency || 'AED',
    features,
    hasFeature,
    refresh: async () => {
      const r = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompany(r.data.find(c => c.id === user?.company_id) || r.data[0] || null);
    }
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCurrency() {
  return useContext(CompanyContext);
}

export function formatCurrency(amount, symbol = 'AED') {
  if (amount == null || isNaN(amount)) return `${symbol} 0.00`;
  return `${symbol} ${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
